import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from '../Users/redux/actions';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import { isCanceledResponse } from '../../../middleware/api';

function AdminOrgGroupsTab(props) {
  const globalPageSize = useGlobalPageSize();
  const { getOrgGroups, onNavigate } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [orgGroups, setOrgGroups] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState('name');
  const [sortType, setSortType] = React.useState('ascending');
  const [currentPage, setCurrentPage] = React.useState(1);

  const loadOrgGroups = React.useCallback(() => {
    if (!getOrgGroups) return;

    setIsLoading(true);

    const params = {
      offset: (currentPage - 1) * globalPageSize,
      limit: globalPageSize,
      sortColumn,
      sortType,
    };

    if (searchTerm) params.search = searchTerm;

    getOrgGroups(params).then(resp => {
      // Superseded by a newer search/page/sort request, which will fill the table.
      if (isCanceledResponse(resp)) {
        return;
      }
      const data = resp?.response || {};
      const groupsData = Array.isArray(data) ? data : data.data || [];
      setOrgGroups(groupsData);
      setPagination({
        totalRecords: data.totalRecords || groupsData.length,
        offset: (currentPage - 1) * globalPageSize,
        limit: globalPageSize,
      });
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [currentPage, globalPageSize, searchTerm, sortColumn, sortType, getOrgGroups]);

  React.useEffect(() => {
    loadOrgGroups();
  }, [loadOrgGroups]);

  const onSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const onOffsetChange = (newOffset) => {
    const newPage = Math.floor(newOffset / globalPageSize) + 1;
    setCurrentPage(newPage);
  };

  const onSortingChange = (sortingState) => {
    if (sortingState && sortingState.length > 0) {
      const columnId = sortingState[0].id;
      setSortColumn(columnId);
      setSortType(sortingState[0].desc ? 'descending' : 'ascending');
      setCurrentPage(1);
    }
  };

  const handleRowClick = (rowData) => {
    if (onNavigate) {
      onNavigate(`/admin/org-groups/${rowData.uuid || rowData.id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('name', {
      header: Strings.name || 'Name',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('controlling_org', {
      header: Strings.controllingOrg || 'Controlling Org',
      enableSorting: false,
      cell: info => {
        const controllingOrg = info.getValue();
        return controllingOrg?.name || '-';
      },
    }),
    columnHelper.accessor('organization_count', {
      header: Strings.organizations || 'Organizations',
      cell: info => info.getValue() || 0,
    }),
    columnHelper.accessor('maintainers', {
      header: Strings.maintainers || 'Maintainers',
      enableSorting: false,
      cell: info => {
        const maintainers = info.getValue();
        if (!maintainers || maintainers.length === 0) return '-';
        return maintainers.map(m => `${m.first_name} ${m.last_name}`).join(', ');
      },
    }),
    columnHelper.accessor('created_at', {
      header: Strings.createdAt || 'Created',
      cell: info => formatDate(info.getValue()),
    }),
  ], []);

  const getHeaderComponents = () => (
    <>
      <TextInput
        class="search"
        placeholder={Strings.search}
        value={searchTerm}
        onChange={onSearchChange}
      />
      <Button
        class={HEADER_BUTTON_BLUE}
        onClick={() => onNavigate && onNavigate('/admin/org-groups/new')}
        title={`+ ${Strings.create || 'Create'}`}
      />
    </>
  );

  return (
    <>
      <PageHeader title={Strings.orgGroups || 'Org Groups'} right={getHeaderComponents()} />
      <Table3
        data={orgGroups || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onSortingChange={onSortingChange}
        onRowClick={handleRowClick}
        noDataText={Strings.noOrgGroupsFound || 'No org groups found'}
      />
    </>
  );
}

const mapDispatchToProps = dispatch => ({
  getOrgGroups: params => dispatch(actions.getOrgGroups(params, { latest: true })),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(AdminOrgGroupsTab);
